/**
 * ZonesPanel - Configure keyboard zones with different MIDI channels/transpose
 * Spec §5.6: Zones (USER-EDITABLE)
 */

import { useState, useEffect, useMemo } from 'react';
import { useConfigStore, selectActiveTuning, selectMappingMode } from '@/store/configStore';
import { getModuleSpacingQ, getModuleKeys } from '@/lib/hexCoords';
import { deriveDegreeStep, degreeToCents } from '@/lib/tuningUtils';
import type { Zone, ZoneBounds, TuningTable } from '@/types/config';

/** Check if two zones overlap (Q and optional R ranges both intersect) */
function zonesOverlap(a: Zone, b: Zone): boolean {
  // Q ranges must intersect
  if (a.bounds.q_max < b.bounds.q_min || b.bounds.q_max < a.bounds.q_min) return false;
  // R ranges: if either zone has no R bounds, it spans all rows → overlap on R
  const aHasR = a.bounds.r_min !== undefined && a.bounds.r_max !== undefined;
  const bHasR = b.bounds.r_min !== undefined && b.bounds.r_max !== undefined;
  if (aHasR && bHasR) {
    if (a.bounds.r_max! < b.bounds.r_min! || b.bounds.r_max! < a.bounds.r_min!) return false;
  }
  return true;
}

/** Find all overlap pairs in the zone list */
function findOverlaps(zones: Zone[]): [number, number][] {
  const pairs: [number, number][] = [];
  for (let i = 0; i < zones.length; i++) {
    for (let j = i + 1; j < zones.length; j++) {
      const a = zones[i];
      const b = zones[j];
      if (a && b && zonesOverlap(a, b)) {
        pairs.push([i, j]);
      }
    }
  }
  return pairs;
}

interface ZoneEditorProps {
  zone: Zone;
  index: number;
  onSave: (zone: Zone) => void;
  onCancel: () => void;
  maxQ: number;
  maxR: number;
}

function ZoneEditor({ zone, index, onSave, onCancel, maxQ, maxR }: ZoneEditorProps) {
  const [name, setName] = useState(zone.name);
  const [qMin, setQMin] = useState(zone.bounds.q_min);
  const [qMax, setQMax] = useState(zone.bounds.q_max);
  const [useRBounds, setUseRBounds] = useState(zone.bounds.r_min !== undefined);
  const [rMin, setRMin] = useState(zone.bounds.r_min ?? 0);
  const [rMax, setRMax] = useState(zone.bounds.r_max ?? maxR);
  const [channel, setChannel] = useState(zone.channel);
  const [transpose, setTranspose] = useState(zone.transpose);

  const activeTuning = useConfigStore(selectActiveTuning);
  const mappingMode = useConfigStore(selectMappingMode);

  // Degree-based transpose state (used when tuning enabled)
  const [transposeDegrees, setTransposeDegrees] = useState(() => {
    if (activeTuning) {
      const cents = zone.transpose_cents ?? zone.transpose * 1000;
      return deriveDegreeStep(cents, activeTuning);
    }
    return zone.transpose;
  });

  // Cents equivalent of current degree transpose (for display)
  const transposeCentsDisplay = useMemo(() => {
    if (!activeTuning) return 0;
    return degreeToCents(transposeDegrees, activeTuning);
  }, [activeTuning, transposeDegrees]);

  // Max degree range (±3 periods)
  const maxDegrees = activeTuning ? activeTuning.notes_per_period * 3 : 24;

  // Sync editing bounds to zone preview for keyboard visualization
  const setZonePreview = useConfigStore((state) => state.setZonePreview);
  useEffect(() => {
    const bounds: ZoneBounds = { q_min: qMin, q_max: qMax };
    if (useRBounds) {
      bounds.r_min = rMin;
      bounds.r_max = rMax;
    }
    setZonePreview(bounds);
    return () => setZonePreview(null);
  }, [qMin, qMax, useRBounds, rMin, rMax, setZonePreview]);

  const handleSave = () => {
    // Auto-swap if min > max
    const sortedQ = [qMin, qMax].sort((a, b) => a - b);
    const bounds: ZoneBounds = {
      q_min: sortedQ[0] ?? qMin,
      q_max: sortedQ[1] ?? qMax,
    };
    if (useRBounds) {
      const sortedR = [rMin, rMax].sort((a, b) => a - b);
      bounds.r_min = sortedR[0] ?? rMin;
      bounds.r_max = sortedR[1] ?? rMax;
    }

    // Compute transpose fields
    let savedTranspose = transpose;
    let savedTransposeCents: number | undefined = zone.transpose_cents;
    if (activeTuning) {
      savedTransposeCents = degreeToCents(transposeDegrees, activeTuning);
      savedTranspose = Math.round(savedTransposeCents / 1000);
    }

    const saved: Zone = {
      name: name.trim() || `Zone ${index + 1}`,
      bounds,
      channel,
      transpose: savedTranspose,
      // Preserve transpose_cents for microtonal round-trip
      ...(savedTransposeCents !== undefined ? { transpose_cents: savedTransposeCents } : {}),
      // Preserve existing note_mapping (edited via NoteMappingPanel)
      ...(zone.note_mapping ? { note_mapping: zone.note_mapping } : {}),
    };
    onSave(saved);
  };

  const inputStyle = { width: '60px', padding: '4px', fontSize: '13px' };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '4px', fontSize: '13px' };

  return (
    <div
      style={{
        padding: '12px',
        backgroundColor: '#e9ecef',
        borderRadius: '4px',
        marginBottom: '12px',
      }}
    >
      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 'bold' }}>
        {index >= 0 ? `Edit Zone ${index + 1}` : 'New Zone'}
      </h4>

      {/* Name */}
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>
          Name (max 32 chars):
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 32))}
          placeholder="Zone name"
          style={{
            width: '100%',
            padding: '6px',
            fontSize: '13px',
            border: '1px solid #e9ecef',
            borderRadius: '4px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Q Bounds (required) */}
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>
          Q Bounds (horizontal):
        </label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="number"
            min={0}
            max={maxQ}
            value={qMin}
            onChange={(e) => setQMin(Math.max(0, Math.min(maxQ, parseInt(e.target.value) || 0)))}
            style={inputStyle}
          />
          <span style={{ fontSize: '13px' }}>to</span>
          <input
            type="number"
            min={0}
            max={maxQ}
            value={qMax}
            onChange={(e) => setQMax(Math.max(0, Math.min(maxQ, parseInt(e.target.value) || 0)))}
            style={inputStyle}
          />
        </div>
      </div>

      {/* R Bounds (optional) */}
      <div style={{ marginBottom: '12px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            cursor: 'pointer',
            marginBottom: '4px',
          }}
        >
          <input
            type="checkbox"
            checked={useRBounds}
            onChange={(e) => setUseRBounds(e.target.checked)}
          />
          Limit R Bounds (vertical):
        </label>
        {useRBounds && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
            <input
              type="number"
              min={0}
              max={maxR}
              value={rMin}
              onChange={(e) => setRMin(Math.max(0, Math.min(maxR, parseInt(e.target.value) || 0)))}
              style={inputStyle}
            />
            <span style={{ fontSize: '13px' }}>to</span>
            <input
              type="number"
              min={0}
              max={maxR}
              value={rMax}
              onChange={(e) => setRMax(Math.max(0, Math.min(maxR, parseInt(e.target.value) || 0)))}
              style={inputStyle}
            />
          </div>
        )}
      </div>

      {/* Channel */}
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>
          MIDI Channel (0-15):
        </label>
        <input
          type="number"
          min={0}
          max={15}
          value={channel}
          onChange={(e) => setChannel(Math.max(0, Math.min(15, parseInt(e.target.value) || 0)))}
          style={inputStyle}
        />
      </div>

      {/* Transpose */}
      <div style={{ marginBottom: '12px' }}>
        {mappingMode === 'microtonal' ? (
          <>
            <label style={labelStyle}>
              Transpose (degrees):
            </label>
            <input
              type="number"
              min={-maxDegrees}
              max={maxDegrees}
              value={transposeDegrees}
              onChange={(e) => setTransposeDegrees(Math.max(-maxDegrees, Math.min(maxDegrees, parseInt(e.target.value) || 0)))}
              style={{ width: '80px', padding: '4px', fontSize: '13px' }}
            />
            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>
              {transposeDegrees > 0 ? `+${transposeDegrees}` : transposeDegrees} deg
              {' '}= {(transposeCentsDisplay / 10).toFixed(1)}c
            </span>
          </>
        ) : (
          <>
            <label style={labelStyle}>
              Transpose (-24 to +24 semitones):
            </label>
            <input
              type="number"
              min={-24}
              max={24}
              value={transpose}
              onChange={(e) => setTranspose(Math.max(-24, Math.min(24, parseInt(e.target.value) || 0)))}
              style={{ width: '80px', padding: '4px', fontSize: '13px' }}
            />
            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>
              {transpose > 0 ? `+${transpose}` : transpose} semitones
            </span>
          </>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button
          onClick={handleSave}
          style={{
            padding: '6px 16px',
            fontSize: '13px',
            color: '#fff',
            backgroundColor: '#28a745',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Save
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '6px 16px',
            fontSize: '13px',
            color: '#333',
            backgroundColor: '#e9ecef',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function formatZoneTranspose(zone: Zone, activeTuning: TuningTable | null): string {
  if (activeTuning) {
    const cents = zone.transpose_cents ?? zone.transpose * 1000;
    const deg = deriveDegreeStep(cents, activeTuning);
    return `${deg > 0 ? '+' : ''}${deg} deg`;
  }
  return `${zone.transpose > 0 ? '+' : ''}${zone.transpose}`;
}

export function ZonesPanel() {
  const zones = useConfigStore((state) => state.zones);
  const columns = useConfigStore((state) => state.columns);
  const rows = useConfigStore((state) => state.rows);
  const keysPerModule = useConfigStore((state) => state.keysPerModule);
  const addZone = useConfigStore((state) => state.addZone);
  const updateZone = useConfigStore((state) => state.updateZone);
  const removeZone = useConfigStore((state) => state.removeZone);
  const reorderZones = useConfigStore((state) => state.reorderZones);
  const activeTuning = useConfigStore(selectActiveTuning);
  const mappingMode = useConfigStore(selectMappingMode);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Calculate max coordinates based on grid size and module type
  const moduleKeys = getModuleKeys(keysPerModule);
  const spacingQ = getModuleSpacingQ(keysPerModule);
  const maxLocalQ = Math.max(...moduleKeys.map((k) => k.hex.q));
  const maxQ = (columns - 1) * spacingQ + maxLocalQ;
  const maxR = rows * 3 + (rows > 1 ? rows - 1 : 0);

  const handleAddZone = () => {
    setIsAdding(true);
    setEditingIndex(null);
  };

  const handleSaveNewZone = (zone: Zone) => {
    addZone(zone);
    setIsAdding(false);
  };

  const handleEditZone = (index: number) => {
    setEditingIndex(index);
    setIsAdding(false);
  };

  const handleSaveEditedZone = (zone: Zone) => {
    if (editingIndex !== null) {
      updateZone(editingIndex, zone);
      setEditingIndex(null);
    }
  };

  const handleDeleteZone = (index: number) => {
    removeZone(index);
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      reorderZones(index, index - 1);
    }
  };

  const handleMoveDown = (index: number) => {
    if (index < zones.length - 1) {
      reorderZones(index, index + 1);
    }
  };

  const overlaps = useMemo(() => zones.length >= 2 ? findOverlaps(zones) : [], [zones]);

  return (
    <>
      {/* Zone list */}
      {zones.length === 0 && !isAdding && (
        <div style={{ marginBottom: '12px', fontSize: '13px', color: '#6c757d' }}>
          No zones defined. Keys use the default MIDI channel.
        </div>
      )}

      {zones.map((zone, index) => (
        <div key={index}>
          {editingIndex === index ? (
            <ZoneEditor
              zone={zone}
              index={index}
              onSave={handleSaveEditedZone}
              onCancel={() => setEditingIndex(null)}
              maxQ={maxQ}
              maxR={maxR}
            />
          ) : (
            <div
              style={{
                padding: '8px',
                marginBottom: '8px',
                backgroundColor: '#f9f9f9',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                    {index + 1}. {zone.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>
                    Q: {zone.bounds.q_min}-{zone.bounds.q_max}
                    {zone.bounds.r_min !== undefined && `, R: ${zone.bounds.r_min}-${zone.bounds.r_max}`}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>
                    Channel: {zone.channel}, Transpose: {formatZoneTranspose(zone, activeTuning)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    style={{
                      padding: '2px 6px',
                      fontSize: '11px',
                      cursor: index === 0 ? 'not-allowed' : 'pointer',
                      opacity: index === 0 ? 0.5 : 1,
                    }}
                    title="Move up (higher priority)"
                  >
                    Up
                  </button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === zones.length - 1}
                    style={{
                      padding: '2px 6px',
                      fontSize: '11px',
                      cursor: index === zones.length - 1 ? 'not-allowed' : 'pointer',
                      opacity: index === zones.length - 1 ? 0.5 : 1,
                    }}
                    title="Move down (lower priority)"
                  >
                    Down
                  </button>
                  <button
                    onClick={() => handleEditZone(index)}
                    style={{ padding: '2px 6px', fontSize: '11px', cursor: 'pointer' }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteZone(index)}
                    style={{
                      padding: '2px 6px',
                      fontSize: '11px',
                      color: '#dc3545',
                      cursor: 'pointer',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* New zone editor */}
      {isAdding && (
        <ZoneEditor
          zone={{
            name: `Zone ${zones.length + 1}`,
            bounds: { q_min: 0, q_max: maxQ },
            channel: 0,
            transpose: 0,
          }}
          index={-1}
          onSave={handleSaveNewZone}
          onCancel={() => setIsAdding(false)}
          maxQ={maxQ}
          maxR={maxR}
        />
      )}

      {/* Add button */}
      {!isAdding && editingIndex === null && (
        <button
          onClick={handleAddZone}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '13px',
            color: '#fff',
            backgroundColor: '#007bff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '12px',
          }}
        >
          + Add Zone
        </button>
      )}

      {/* Overlap warning */}
      {overlaps.length > 0 && (() => {
        return (
          <div
            style={{
              padding: '8px',
              marginBottom: '8px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#856404',
            }}
          >
            <strong>Overlapping zones:</strong>
            {overlaps.map(([i, j]) => {
              const a = zones[i];
              const b = zones[j];
              return (
                <div key={`${i}-${j}`} style={{ marginTop: '2px' }}>
                  {a?.name ?? `Zone ${i + 1}`} and {b?.name ?? `Zone ${j + 1}`} overlap
                  — keys in overlap use {a?.name ?? `Zone ${i + 1}`} (higher priority)
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Info box */}
      <div
        style={{
          padding: '8px',
          backgroundColor: '#e7f3ff',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#0056b3',
        }}
      >
        <strong>Priority:</strong> Zones are checked in order (1st has highest priority).
        Keys matching multiple zones use the first match.
        <br />
        <strong>Bounds:</strong> Q (horizontal) is required. R (vertical) is optional - if omitted, zone applies to all rows.
        <br />
        <strong>Mapping:</strong> Configure per-zone note mapping in the Layout tab (Note Mapping panel).
        {mappingMode === 'microtonal' && (
          <>
            <br />
            <strong>Tuning:</strong> Transpose is in scale degrees of the active tuning.
          </>
        )}
      </div>
    </>
  );
}
